import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageMedia from '../components/PageMedia';
import { usePageContents } from '../contexts/PageContentContext';

const AboutGTC: React.FC = () => {
  const { getPageContent, loading } = usePageContents();
  const content = getPageContent('about_gtc');

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex items-center justify-center min-h-[400px] mt-20">
          <div className="text-gray-500">로딩 중...</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* Page Header with Image */}
      <div className="relative text-white py-24 mt-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=1920&h=600&fit=crop&q=80"
            alt="Education"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>

        <div className="max-w-6xl mx-auto px-8 relative z-10">
          <p className="text-gray-200 mb-3 text-sm font-medium tracking-wider">센터소개</p>
          <h1 className="text-5xl md:text-6xl font-bold mb-4">DGTC소개</h1>
          <div className="w-20 h-1 bg-white/70"></div>
        </div>
      </div>

      {/* Mission Section - White Background */}
      <div className="flex-1 py-20 bg-white">
        <div className="max-w-6xl mx-auto px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4 leading-relaxed">
              {content?.title_main || '국제교육을 통한'}
            </h2>
            <h2 className="text-4xl md:text-5xl font-bold text-green-600 mb-8 leading-relaxed">
              {content?.title_sub || '글로벌 창의인재 양성'}
            </h2>
            <p className="text-xl text-gray-500 mb-6">
              {content?.title_eng || 'Fostering Creative Global Leaders through International Education'}
            </p>
            <div className="w-20 h-1 bg-green-500 mx-auto"></div>
          </div>
        </div>
      </div>

      {/* About Section - Gray Background */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-8">
          <div className="max-w-4xl mx-auto space-y-8">
            {content?.description ? (
              <div
                className="text-xl text-gray-700 leading-relaxed text-center prose prose-lg max-w-none"
                dangerouslySetInnerHTML={{ __html: content.description }}
              />
            ) : (
              <>
                <p className="text-xl text-gray-700 leading-relaxed text-center">
                  동두천시 글로벌인재교육센터는 <span className="font-bold text-gray-900">글로벌 시대에 필요한 언어 능력, 문화 감수성, 창의적 사고</span>를 함께 키우는 국제교육 전문기관입니다.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed text-center">
                  유아부터 성인까지 모든 세대가 함께 배우며 성장할 수 있는 열린 배움의 공간으로, 언어교육을 넘어 세계 시민으로 성장하기 위한 포괄적 글로벌 교육 프로그램을 운영하고 있습니다.
                </p>
              </>
            )}

            {/* 이미지 또는 영상 표시 */}
            <PageMedia
              imageUrl={content?.image_url}
              videoUrl={content?.video_url}
              mediaType={content?.media_type}
              videoAutoplay={content?.video_autoplay}
              alt="DGTC소개 미디어"
            />
          </div>
        </div>
      </div>

      {/* Core Values Section - White Background */}
      <div className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-8">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="text-3xl">🌱</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
              핵심 가치
            </h2>
            <p className="text-gray-600">Core Values</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Value 01 */}
            <div className="group">
              <div className="bg-gradient-to-br from-green-50 to-white border border-gray-200 rounded-2xl p-8 h-full hover:shadow-xl transition-all duration-300">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-2">창의성</h3>
                    <p className="text-sm text-green-600 font-medium">Creativity</p>
                  </div>
                  <div className="text-4xl">💡</div>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  스스로 배우고 탐구하며<br />새로운 가치를 창출하는 학습
                </p>
              </div>
            </div>

            {/* Value 02 */}
            <div className="group">
              <div className="bg-gradient-to-br from-teal-50 to-white border border-gray-200 rounded-2xl p-8 h-full hover:shadow-xl transition-all duration-300">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-2">융합성</h3>
                    <p className="text-sm text-teal-600 font-medium">Convergence</p>
                  </div>
                  <div className="text-4xl">🔗</div>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  언어·STEAM·AI 등<br />다양한 영역을 융합한 통합형 교육
                </p>
              </div>
            </div>

            {/* Value 03 */}
            <div className="group">
              <div className="bg-gradient-to-br from-blue-50 to-white border border-gray-200 rounded-2xl p-8 h-full hover:shadow-xl transition-all duration-300">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-2">소통과 협력</h3>
                    <p className="text-sm text-blue-600 font-medium">Communication & Collaboration</p>
                  </div>
                  <div className="text-4xl">🤝</div>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  다문화를 이해하고 존중하는<br />글로벌 시민 양성
                </p>
              </div>
            </div>

            {/* Value 04 */}
            <div className="group">
              <div className="bg-gradient-to-br from-emerald-50 to-white border border-gray-200 rounded-2xl p-8 h-full hover:shadow-xl transition-all duration-300">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-2">지속가능성</h3>
                    <p className="text-sm text-emerald-600 font-medium">Sustainability</p>
                  </div>
                  <div className="text-4xl">🌍</div>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  지역과 세계가 함께 성장하는<br />교육 생태계 구축
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Educational Direction Section - Gray Background */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-8">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="text-3xl">🎓</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
              교육 방향
            </h2>
            <p className="text-gray-600">Educational Direction</p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="divide-y divide-gray-100">
                <div className="p-6 hover:bg-green-50/50 transition-colors group">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold group-hover:bg-green-500 group-hover:text-white transition-colors">
                      01
                    </div>
                    <div className="flex-1">
                      <p className="text-lg text-gray-800 leading-relaxed">
                        유치~성인까지 <span className="font-semibold text-green-600">전 연령 대상의 맞춤형 국제교육</span> 제공
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-6 hover:bg-green-50/50 transition-colors group">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold group-hover:bg-green-500 group-hover:text-white transition-colors">
                      02
                    </div>
                    <div className="flex-1">
                      <p className="text-lg text-gray-800 leading-relaxed">
                        <span className="font-semibold text-green-600">언어·STEAM·문화체험·AI 융합형</span> 학습 프로그램 운영
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-6 hover:bg-green-50/50 transition-colors group">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold group-hover:bg-green-500 group-hover:text-white transition-colors">
                      03
                    </div>
                    <div className="flex-1">
                      <p className="text-lg text-gray-800 leading-relaxed">
                        <span className="font-semibold text-green-600">온·오프라인 통합(하이브리드)</span> 교육환경 구축
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-6 hover:bg-green-50/50 transition-colors group">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold group-hover:bg-green-500 group-hover:text-white transition-colors">
                      04
                    </div>
                    <div className="flex-1">
                      <p className="text-lg text-gray-800 leading-relaxed">
                        지역사회와 함께 성장하는 <span className="font-semibold text-green-600">글로벌 교육 플랫폼</span> 실현
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Vision Section - Green Background with Image */}
      <div className="py-20 text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?w=1920&h=600&fit=crop&q=80"
            alt="Global Education"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-green-700/90 to-teal-800/90"></div>
        </div>

        <div className="max-w-6xl mx-auto px-8 relative z-10">
          <div className="text-center">
            <div className="inline-block mb-6">
              <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
                <span className="text-2xl">🌐</span>
                <span className="font-semibold text-lg">DGTC의 비전</span>
              </div>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6 leading-relaxed">
              "배움이 즐겁고,<br />
              세계로 이어지는 글로벌 교육도시<br />
              동두천을 만들어갑니다."
            </h2>
            <p className="text-xl text-green-100 max-w-3xl mx-auto leading-relaxed">
              Vision Statement
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AboutGTC;
